import type { HTMLAttributes, ReactNode } from 'react';
import { Check } from 'lucide-react';
import { clsx } from '../clsx';
import styles from './Stepper.module.css';

export interface StepperStep {
  id: string;
  label: string;
  /** Second line in the vertical layout ("DAMOKLES prüft automatisch alle 15 Minuten."). */
  description?: ReactNode | undefined;
}

export interface StepperProps extends Omit<HTMLAttributes<HTMLElement>, 'onClick'> {
  steps: ReadonlyArray<StepperStep>;
  /** Index of the current step; steps before it are done, steps after it open. */
  current: number;
  /** `vertical`: numbered circles with text (Einstellungen 22 domain wizard); `horizontal`: progress bars with circles (Objekt anlegen 01). */
  orientation?: 'vertical' | 'horizontal' | undefined;
  /** Makes done steps clickable (go back in a wizard). */
  onStepClick?: ((index: number) => void) | undefined;
  /** Accessible name of the list ("Schritte"). */
  label?: string | undefined;
}

/** Numbered steps with done / current / open states. */
export function Stepper({
  steps,
  current,
  orientation = 'vertical',
  onStepClick,
  label = 'Schritte',
  className,
  ...rest
}: StepperProps) {
  return (
    <ol
      aria-label={label}
      className={clsx(
        styles.list,
        orientation === 'horizontal' ? styles.horizontal : styles.vertical,
        className,
      )}
      {...rest}
    >
      {steps.map((step, index) => {
        const state = index < current ? 'done' : index === current ? 'current' : 'open';
        const clickable = Boolean(onStepClick) && state === 'done';
        const content = (
          <>
            {orientation === 'horizontal' ? <span className={styles.bar} aria-hidden="true" /> : null}
            <span className={styles.row}>
              <span className={styles.circle} aria-hidden="true">
                {state === 'done' ? (
                  <Check size={orientation === 'horizontal' ? 11 : 12} strokeWidth={2.5} />
                ) : (
                  index + 1
                )}
              </span>
              <span className={styles.text}>
                <span className={styles.label}>{step.label}</span>
                {step.description && orientation === 'vertical' ? (
                  <span className={styles.description}>{step.description}</span>
                ) : null}
              </span>
            </span>
          </>
        );
        return (
          <li
            key={step.id}
            data-state={state}
            aria-current={state === 'current' ? 'step' : undefined}
            className={clsx(styles.step, styles[state])}
          >
            {clickable ? (
              <button type="button" className={styles.button} onClick={() => onStepClick?.(index)}>
                {content}
              </button>
            ) : (
              content
            )}
          </li>
        );
      })}
    </ol>
  );
}
